import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownToLine, ArrowRightLeft, ArrowUpFromLine, CheckCircle2, CircleAlert,
  Clock3, FileWarning, KeyRound, MessageSquareWarning, RotateCcw, Search, SlidersHorizontal,
  TrendingUp, Utensils, WalletCards, WashingMachine, Wrench,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getCurrentUser } from "../lib/auth";
import { staffRoles } from "../lib/staffRoles";
import { TaskDetailModal } from "../components/TaskDetailModal";
import { MovementListModal } from "../components/MovementListModal";
import type { DashboardSummary } from "@apartos/contracts";
import { compareDashboardTasks, type DashboardTaskSort } from "../lib/taskOrdering";

const money = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function DashboardPage() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [attentionFilter, setAttentionFilter] = useState<"all" | "urgent" | "new" | "working">("all");
  const [taskKind, setTaskKind] = useState<DashboardSummary["attention"][number]["kind"] | "all">("all");
  const [taskSource, setTaskSource] = useState<NonNullable<DashboardSummary["attention"][number]["source"]> | "all">("all");
  const [taskApartment, setTaskApartment] = useState("all");
  const [taskAge, setTaskAge] = useState<"all" | "today" | "24h" | "older">("all");
  const [taskSort, setTaskSort] = useState<DashboardTaskSort>("newest");
  const [taskQuery, setTaskQuery] = useState("");
  const [movementList, setMovementList] = useState<"arrival" | "departure" | null>(null);
  const [selectedTask, setSelectedTask] = useState<DashboardSummary["attention"][number] | null>(null);
  const auth = useQuery({ queryKey: ["auth", "me"], queryFn: getCurrentUser, staleTime: 60_000 });
  const currentUser = auth.data && auth.data !== "bootstrap" ? auth.data : null;
  const canSeeFinance = !currentUser || currentUser.role === "owner" || currentUser.role === "accountant";
  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: api.dashboard,
  });
  const updateRequest = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "in_progress" | "resolved" }) => api.updateGuestRequest(id, status),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["guest-requests"] }),
        client.invalidateQueries({ queryKey: ["operational-shift"] }),
      ]);
    },
  });

  if (isPending) return <DashboardSkeleton />;
  if (isError || !data) return <ErrorState />;

  const apartmentTotal = Object.values(data.apartmentStatusCounts).reduce((sum, count) => sum + count, 0);
  const planPercent = data.revenuePlan > 0
    ? Math.min(100, Math.round(data.revenueMonth / data.revenuePlan * 100))
    : 0;
  const currentDate = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
  const kpis = [
    { label: "Заездов к обработке", value: data.arrivals, meta: "сегодня и просроченные", tone: "orange", icon: ArrowDownToLine },
    { label: "Выездов к обработке", value: data.departures, meta: "сегодня и просроченные", tone: "purple", icon: ArrowUpFromLine },
    { label: "Уборок в работе", value: data.cleaningsInProgress, meta: "активные задачи", tone: "yellow", icon: WashingMachine },
    { label: "Свободно сейчас", value: data.availableNow, meta: `из ${apartmentTotal} апартаментов`, tone: "green", icon: CheckCircle2 },
  ] as const;

  const statuses = [
    ["Свободно", data.apartmentStatusCounts.free, "green", "free"],
    ["Проживают", data.apartmentStatusCounts.occupied, "blue", "occupied"],
    ["Заезды", data.apartmentStatusCounts.checkin, "orange", "checkin"],
    ["Выезды", data.apartmentStatusCounts.checkout, "purple", "checkout"],
    ["Уборка", data.apartmentStatusCounts.cleaning, "yellow", "cleaning"],
    ["Ремонт", data.apartmentStatusCounts.repair, "red", "repair"],
  ] as const;
  const taskApartments = [...new Set(data.attention.map((item) => item.apartmentNumber).filter(Boolean))].sort();
  const now = Date.now();
  const taskQueryNormalized = taskQuery.trim().toLocaleLowerCase("ru");
  const visibleAttention = data.attention.filter((item) =>
    (attentionFilter === "all" ? true
      : attentionFilter === "urgent" ? item.tone === "red"
        : attentionFilter === "new" ? item.status === "queued" : item.status === "in_progress")
    && (taskKind === "all" || item.kind === taskKind)
    && (taskSource === "all" || item.source === taskSource)
    && (taskApartment === "all" || item.apartmentNumber === taskApartment)
    && (taskAge === "all" || (() => {
      const createdAt = item.createdAt ? new Date(item.createdAt).getTime() : 0;
      if (!createdAt) return taskAge === "older";
      if (taskAge === "24h") return now - createdAt <= 86_400_000;
      if (taskAge === "older") return now - createdAt > 86_400_000;
      return new Date(createdAt).toDateString() === new Date(now).toDateString();
    })())
    && (!taskQueryNormalized || `${item.title} ${item.subtitle} ${item.apartmentNumber ?? ""}`.toLocaleLowerCase("ru").includes(taskQueryNormalized)))
    .sort(compareDashboardTasks(taskSort));
  const hasTaskFilters = attentionFilter !== "all" || taskKind !== "all" || taskSource !== "all"
    || taskApartment !== "all" || taskAge !== "all" || taskSort !== "newest" || Boolean(taskQuery);
  const resetTaskFilters = () => {
    setAttentionFilter("all"); setTaskKind("all"); setTaskSource("all"); setTaskApartment("all");
    setTaskAge("all"); setTaskSort("newest"); setTaskQuery("");
  };

  return (
    <div className="page dashboard">
      <section className="welcome-row">
        <div>
          <h1>{currentUser ? `Добрый день, ${currentUser.name.split(/\s+/)[0]}` : "Добрый день"}</h1>
          <p>{currentDate} · {currentUser ? staffRoles[currentUser.role].label : "Полный доступ"} · загрузка {data.occupancyPercent}%</p>
        </div>
        <span className="role-workspace-badge">{currentUser ? staffRoles[currentUser.role].short : "Все модули"}</span>
      </section>
      <section className="kpi-grid">
        {kpis.map(({ label, value, meta, tone, icon: Icon }, index) => (
          <article className={`card kpi-card ${index < 2 ? "is-clickable" : ""}`} key={label} onClick={index < 2 ? () => setMovementList(index === 0 ? "arrival" : "departure") : undefined}>
            <div className={`metric-icon tone-${tone}`}><Icon size={19} /></div>
            <div className="kpi-card__meta">{meta}</div>
            <strong>{value}</strong>
            <span>{label}</span>
            {index < 2 && <button type="button">Подробнее</button>}
          </article>
        ))}
      </section>

      <section className="card status-card">
        <div className="card-heading">
          <h2>Состояние номеров</h2>
          <span>{apartmentTotal} апартаментов · загрузка {data.occupancyPercent}%</span>
        </div>
        <div className="status-strip" aria-label="Распределение статусов">
          {statuses.map(([label, count, tone]) => (
            count > 0 && <i key={label} className={`bg-${tone}`} style={{ flex: count }} title={`${label}: ${count}`} />
          ))}
        </div>
        <div className="status-list">
          {statuses.map(([label, count, tone, status]) => (
            <button key={label} onClick={() => navigate(`/apartments?status=${status}`)}><i className={`bg-${tone}`} /><strong>{count}</strong><span>{label}</span></button>
          ))}
        </div>
      </section>

      {canSeeFinance && <section className="finance-grid">
        <article className="revenue-card">
          <div className="card-heading card-heading--light"><span>Доход сегодня</span><TrendingUp size={18} /></div>
          <strong>{money.format(data.revenueToday)}</strong>
          <p>Чистые поступления с учётом возвратов</p>
        </article>
        <article className="card finance-card">
          <span>Доход за месяц</span>
          <strong>{money.format(data.revenueMonth)}</strong>
          <div className="progress"><i style={{ width: `${planPercent}%` }} /></div>
          <small>{planPercent}% от плана {money.format(data.revenuePlan)}</small>
        </article>
        <article className="card occupancy-card">
          <span>Текущая загрузка</span>
          <div className="occupancy-card__body">
            <div className="ring" style={{ "--progress": `${data.occupancyPercent * 3.6}deg` } as React.CSSProperties}><b>{data.occupancyPercent}%</b></div>
            <p><b>{data.occupiedNow}</b> заняты<br /><b>{data.availableNow}</b> свободны</p>
          </div>
        </article>
      </section>}

      <section className="attention-grid">
        <article className="card attention-card">
          <div className="card-heading attention-heading"><div><h2>Задачи смены <span className="count-badge">{data.attention.length}</span></h2><span>{data.operationalShift ? `Ответственный: ${data.operationalShift.acceptedBy}` : "Брони, гости, уборка и сервис в одной очереди"}</span></div>
            <div className="attention-filters" aria-label="Быстрые фильтры"><button className={attentionFilter === "all" ? "is-active" : ""} onClick={() => setAttentionFilter("all")}>Все <b>{data.attention.length}</b></button><button className={attentionFilter === "urgent" ? "is-active" : ""} onClick={() => setAttentionFilter("urgent")}>Срочно <b>{data.attention.filter((item) => item.tone === "red").length}</b></button><button className={attentionFilter === "new" ? "is-active" : ""} onClick={() => setAttentionFilter("new")}>Новые <b>{data.attention.filter((item) => item.status === "queued").length}</b></button><button className={attentionFilter === "working" ? "is-active" : ""} onClick={() => setAttentionFilter("working")}>В работе <b>{data.attention.filter((item) => item.status === "in_progress").length}</b></button></div>
          </div>
          <div className="attention-toolbar">
            <label className="attention-search"><Search size={14} /><input aria-label="Поиск задач" value={taskQuery} onChange={(event) => setTaskQuery(event.target.value)} placeholder="Гость, номер или задача" /></label>
            <div className="attention-toolbar__label"><SlidersHorizontal size={13} /> Фильтры</div>
            <select aria-label="Тип задачи" value={taskKind} onChange={(event) => setTaskKind(event.target.value as typeof taskKind)}><option value="all">Все типы</option><option value="payment">Оплата</option><option value="document">Документы</option><option value="room">Готовность номера</option><option value="cleaning">Уборка</option><option value="repair">Дефекты</option><option value="request">Запросы</option><option value="room_service">Рум-сервис</option></select>
            <select aria-label="Источник задачи" value={taskSource} onChange={(event) => setTaskSource(event.target.value as typeof taskSource)}><option value="all">Все источники</option><option value="booking">Бронирования</option><option value="qr">QR-запросы</option><option value="housekeeping">Уборка</option><option value="room_service">Рум-сервис</option><option value="apartment">Апартаменты</option></select>
            <select aria-label="Апартамент" value={taskApartment} onChange={(event) => setTaskApartment(event.target.value)}><option value="all">Все номера</option>{taskApartments.map((number) => <option key={number} value={number}>Apt {number}</option>)}</select>
            <select aria-label="Период" value={taskAge} onChange={(event) => setTaskAge(event.target.value as typeof taskAge)}><option value="all">За всё время</option><option value="today">Созданы сегодня</option><option value="24h">Последние 24 часа</option><option value="older">Старше суток</option></select>
            <select aria-label="Сортировка" value={taskSort} onChange={(event) => setTaskSort(event.target.value as DashboardTaskSort)}><option value="newest">Сначала новые</option><option value="urgent">Сначала срочные</option><option value="apartment">По номеру</option></select>
            {hasTaskFilters && <button type="button" className="attention-reset" onClick={resetTaskFilters}><RotateCcw size={13} /> Сбросить</button>}
          </div>
          <div className="attention-result"><span>Показано <strong>{visibleAttention.length}</strong> из {data.attention.length}</span><em>{taskSort === "newest" ? "новые сверху" : taskSort === "urgent" ? "срочные сверху" : "по апартаментам"}</em></div>
          {!data.operationalShift && <div className="shift-warning"><Clock3 size={16}/><div><strong>Операционная смена не принята</strong><span>Примите смену в верхней панели, чтобы закрепить очередь задач за собой.</span></div></div>}
          {data.operationalShift?.handedOverFrom && <div className="shift-handover"><ArrowRightLeft size={14}/> {data.attention.length} незакрытых задач переданы от {data.operationalShift.handedOverFrom}</div>}
          {visibleAttention.map((item) => {
            const Icon = item.kind === "payment" ? WalletCards
              : item.kind === "document" ? FileWarning
                : item.kind === "room" ? KeyRound
                  : item.kind === "repair" ? Wrench
                    : item.kind === "request" ? MessageSquareWarning
                      : item.kind === "room_service" ? Utensils : WashingMachine;
            const requestId = item.id.startsWith("request-") ? item.id.slice("request-".length) : null;
            return <article className={`attention-row attention-row--enhanced attention-row--${item.tone}`} key={item.id}>
              <div className={`metric-icon tone-${item.tone}`}><Icon size={17} /></div>
              <button className="attention-row__body" onClick={() => setSelectedTask(item)}><small>{item.source === "qr" ? "QR-запрос" : item.source === "room_service" ? "Доставка" : item.source === "housekeeping" ? "Уборка" : "Операция"}</small><strong>{item.title}</strong><span>{item.subtitle}</span><em>{item.assignedTo}{item.createdAt ? ` · ${relativeTime(item.createdAt)}` : ""}</em></button>
              {requestId && item.actionable ? <div className="attention-row__actions">
                {item.status === "queued" && <button disabled={updateRequest.isPending} onClick={() => updateRequest.mutate({ id: requestId, status: "in_progress" })}>В работу</button>}
                <button className="is-primary" disabled={updateRequest.isPending} onClick={() => updateRequest.mutate({ id: requestId, status: "resolved" })}>Готово</button>
              </div> : <button className={`tag tone-${item.tone}`} onClick={() => setSelectedTask(item)}>Подробнее</button>}
            </article>;
          })}
          {visibleAttention.length === 0 && <div className="empty-state"><CheckCircle2 size={22} /><h2>Всё спокойно</h2><p>В выбранной категории нет незакрытых задач.</p></div>}
        </article>
        <article className="card timeline-card">
          <div className="card-heading"><h2>Расписание дня</h2><span>{data.schedule.length} событий</span></div>
          {data.schedule.map((item) => {
            const Icon = item.type === "arrival" ? ArrowDownToLine : item.type === "departure" ? ArrowUpFromLine : WashingMachine;
            const tone = item.status === "completed" ? "green" : item.status === "ready" ? "blue" : "orange";
            return <button className="timeline-row timeline-row--action" key={`${item.type}-${item.id}`} onClick={() => navigate(item.path)}>
              <time>{item.time}</time><i className={`bg-${tone}`} />
              <span><strong>{item.title}</strong><small>{item.subtitle}</small></span>
              <Icon size={15} />
            </button>;
          })}
          {data.schedule.length === 0 && <div className="empty-state"><Clock3 size={22} /><h2>Событий пока нет</h2><p>Очередь текущего дня пуста.</p></div>}
        </article>
      </section>
      {selectedTask && <TaskDetailModal task={selectedTask} onClose={() => setSelectedTask(null)} onOpenSection={(path) => { setSelectedTask(null); navigate(path); }} />}
      {movementList && <MovementListModal direction={movementList} items={movementList === "arrival" ? data.arrivalsList : data.departuresList} onClose={() => setMovementList(null)} onOpen={() => { setMovementList(null); navigate("/checkinout"); }} />}
    </div>
  );
}

function relativeTime(value: string): string {
  const rawMinutes = Math.round((Date.now() - new Date(value).getTime()) / 60_000);
  if (rawMinutes < -1) {
    const futureMinutes = Math.abs(rawMinutes);
    if (futureMinutes < 60) return `через ${futureMinutes} мин`;
    const futureHours = Math.floor(futureMinutes / 60);
    if (futureHours < 24) return `через ${futureHours} ч`;
    return `через ${Math.floor(futureHours / 24)} дн`;
  }
  const minutes = Math.max(0, rawMinutes);
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч`;
  return `${Math.floor(hours / 24)} дн`;
}

function DashboardSkeleton() {
  return <div className="page"><div className="skeleton skeleton--title" /><div className="kpi-grid">{[1, 2, 3, 4].map((key) => <div className="card skeleton skeleton--card" key={key} />)}</div></div>;
}

function ErrorState() {
  return <div className="empty-state"><CircleAlert size={24} /><h2>Не удалось загрузить сводку</h2><p>Проверьте, что API запущен на порту 3001.</p></div>;
}
